import { useState, type CSSProperties } from "react";
import { Bell, CircleCheckBig, CircleDot } from "lucide-react";
import DurationSlider from "@/components/foundations/inputs/DurationSlider/DurationSlider";
import {
  TimeInputField,
  formatTimeValue,
  roundedNowTimeParts,
} from "@/components/foundations/inputs/TimeInputField/TimeInputField";
import {
  StartStopTimeDialog,
  type StartStopTarget,
} from "@/components/tasks/timing/StartStopTimeDialog/StartStopTimeDialog";
import { Label } from "@/components/ui/label";
import type { TaskTimingColors } from "@/components/tasks/timing/TaskTimingFields/TaskTimingFields";

export type PatternTimingValue = {
  startTime: string;
  durationMinutes: string;
};

type Props = {
  value: PatternTimingValue;
  onChange: (
    value: PatternTimingValue,
    reason: "start-time" | "duration"
  ) => void;
  onDurationBlur?: () => void;
  alertEnabled?: boolean;
  onAlertEnabledChange?: (value: boolean) => void;
  colors: TaskTimingColors;
};

export function derivedPatternEndTime(
  startTime: string,
  durationMinutes: string
) {
  if (!startTime) return "";
  const [hours, minutes] = startTime.split(":").map(Number);
  const duration = Number.parseInt(durationMinutes, 10);
  if (
    !Number.isFinite(hours) ||
    !Number.isFinite(minutes) ||
    !Number.isFinite(duration) ||
    duration < 1
  )
    return "";
  const total = (hours * 60 + minutes + duration) % (24 * 60);
  return formatTimeValue(Math.floor(total / 60), total % 60);
}

export default function PatternTimingFields({
  value,
  onChange,
  onDurationBlur,
  alertEnabled = false,
  onAlertEnabledChange,
  colors,
}: Props) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTarget, setDialogTarget] = useState<StartStopTarget>("start");
  const endTime = derivedPatternEndTime(value.startTime, value.durationMinutes);
  const style = {
    "--section-bar": colors.bar,
    "--section-soft": colors.soft,
    "--section-ink": colors.ink,
  } as CSSProperties;
  const openDialog = (target: StartStopTarget) => {
    setDialogTarget(target);
    setDialogOpen(true);
  };
  const setStartTime = (startTime: string) =>
    onChange({ ...value, startTime }, "start-time");
  const setDuration = (durationMinutes: string) =>
    onChange({ ...value, durationMinutes }, "duration");
  return (
    <>
      <div className="timing-fields pattern-timing-fields" style={style}>
        <div className="timing-time-fields">
          <PatternTimeField
            label="Start"
            value={value.startTime}
            onChange={setStartTime}
            onClear={() => setStartTime("")}
            onClick={() => openDialog("start")}
            colors={colors}
            icon={<CircleDot />}
            alarm={alertEnabled}
            onAlarmChange={onAlertEnabledChange}
          />
          <PatternTimeField
            label="End"
            value={endTime}
            onChange={() => undefined}
            onClear={() => undefined}
            onClick={() => undefined}
            colors={colors}
            icon={<CircleCheckBig />}
            readOnly
          />
        </div>
        <DurationSlider
          value={value.durationMinutes}
          onChange={setDuration}
          onBlur={onDurationBlur}
        />
      </div>
      <StartStopTimeDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initialTarget={dialogTarget}
        dateKey=""
        splitDate={false}
        scheduledTime={value.startTime || null}
        colors={colors}
        onConfirm={({ target, when }) => {
          const nextTime = when
            ? formatTimeValue(when.getHours(), when.getMinutes())
            : "";
          if (target === "start") setStartTime(nextTime);
        }}
      />
    </>
  );
}

function PatternTimeField({
  label,
  value,
  onChange,
  onClear,
  onClick,
  colors,
  icon,
  alarm = false,
  onAlarmChange,
  readOnly = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onClear: () => void;
  onClick: () => void;
  colors: TaskTimingColors;
  icon: React.ReactNode;
  alarm?: boolean;
  onAlarmChange?: (value: boolean) => void;
  readOnly?: boolean;
}) {
  return (
    <div className="field">
      <div className="field-header">
        <Label className="label">{label}</Label>
        {value && !readOnly && (
          <button
            type="button"
            className="clear-button"
            aria-label={`Clear ${label.toLowerCase()}`}
            onClick={onClear}
          >
            ×
          </button>
        )}
      </div>
      <div className="time-row">
        <TimeInputField
          value={value}
          onChange={onChange}
          onClick={readOnly ? undefined : onClick}
          borderColor={colors.bar}
          leadingIcon={icon}
          use24Hour
        />
        {onAlarmChange && (
          <button
            type="button"
            className="alarm"
            aria-label={alarm ? "Turn alarm off" : "Turn alarm on"}
            title={alarm ? "Turn alarm off" : "Turn alarm on"}
            onClick={() => onAlarmChange(!alarm)}
            style={{ opacity: alarm ? 1 : 0.45 }}
          >
            <Bell />
          </button>
        )}
      </div>
    </div>
  );
}

export function patternNowValue(value: PatternTimingValue): PatternTimingValue {
  const { h, m } = roundedNowTimeParts();
  return { ...value, startTime: formatTimeValue(h, m) };
}
